import { ColourVariant } from "@shared/ui.design-system"
import { LoadingSpinner, Typography } from "@shared/ui.primitives"
import clsx from "clsx"

import { containerOverlay, loadingOverlayWrapper } from "./LoadingOverlay.css"

type LoadingOverlayProps = {
  message: string
  spinnerColour?: ColourVariant
  textColour?: ColourVariant
}

/**
 * `LoadingOverlay` is a reusable UI component that displays a centred loading spinner
 * and optional message over its parent container. It is typically used to indicate
 * a local loading state while transitioning between views or fetching data.
 *
 * ## Positioning
 * - This overlay uses `position: absolute` and fills its parent container (`inset: 0`).
 * - **Important:** The parent element must have `position: relative` for the overlay to appear correctly.
 *
 * ## Use case
 * Wrap a section of your UI with a relatively positioned container:
 *
 * ```tsx
 * <div style={{ position: "relative" }}>
 *   {isLoading && <LoadingOverlay message="Loading your data..." />}
 *   <ActualContent />
 * </div>
 * ```
 *
 * @param {Object} props - Component props
 * @param {string} props.message - Message to display beneath the spinner
 * @param {ColourVariant} [props.textColour="primary-dark"] - Optional text colour variant from the design system
 * @param {ColourVariant} [props.spinnerColour="accent-deep"] - Optional spinner colour variant from the design system
 *
 * @returns {JSX.Element} The rendered loading overlay component
 */
export const LoadingOverlay = ({
  message,
  spinnerColour = "accent-deep",
  textColour = "primary-dark",
}: LoadingOverlayProps): JSX.Element => {
  return (
    <div className={clsx(containerOverlay, loadingOverlayWrapper)}>
      <LoadingSpinner colour={spinnerColour} />
      <Typography colour={textColour} variant="body-md">
        {message}
      </Typography>
    </div>
  )
}
